import { MessageSquare } from 'lucide-react';
import type { ShareRow } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import type { Column } from '#client/components/ui/DataTable';
import { DataTable } from '#client/components/ui/DataTable';
import { DensityControl } from '#client/components/ui/DensityControl';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { Pagination } from '#client/components/ui/Pagination';
import { ShareStatusBadge } from '#client/components/ui/StatusBadge';
import { useResourceList } from '#client/hooks/useResourceList';
import { formatDateTime, dash } from '#client/lib/format';
import { sentBy } from './sent-by';
import { ENDPOINT } from '#client/lib/endpoints';

/**
 * Every report this lab has sent on WhatsApp.
 *
 * Admin only, and separate from the per-visit card for the same reason
 * `finance.view` is separate from `billing.view`: seeing what happened to *this*
 * patient's report is part of doing the job at the counter, while a list of
 * every patient's phone number is a wider view than that requires.
 *
 * Append-only, like the stock ledger it is modelled on. A retry is a new row, so
 * the count of rows is the count of messages actually sent — which is the figure
 * anybody asking about this afterwards wants.
 */
export function ShareAuditPage() {
  const shares = useResourceList<ShareRow>({ path: ENDPOINT.reportShares });

  const columns: Column<ShareRow>[] = [
    { key: 'created_at', header: 'When', render: (row) => formatDateTime(row.created_at) },
    { key: 'patient_ref', header: 'Patient', render: (row) => row.patient_ref },
    {
      key: 'to_e164',
      header: 'To',
      render: (row) => <span className="tnum">+{row.to_e164}</span>,
    },
    { key: 'report_names', header: 'Tests', render: (row) => row.report_names },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <>
          <ShareStatusBadge status={row.status} />
          {row.attempt_no > 1 && <span className="c-muted"> attempt {row.attempt_no}</span>}
        </>
      ),
    },
    {
      key: 'shared_by',
      header: 'Sent by',
      render: sentBy,
    },
    {
      key: 'error_message',
      header: 'Note',
      // The sentence the front desk was shown, not the Graph code. The code and
      // the trace id are on the row for support; this column is for reading.
      render: (row) => dash(row.error_message),
    },
  ];

  return (
    <Page
      title="WhatsApp log"
      subtitle="Every report sent to a patient, and what happened to it."
      secondaryActions={<DensityControl />}
    >
      <Card flush>
        <DataTable
          caption="WhatsApp sends"
          columns={columns}
          rows={shares.items}
          rowKey={(row) => row.id}
          loading={shares.loading}
          emptyState={
            <EmptyState
              icon={<MessageSquare />}
              title="Nothing has been sent yet"
              body="When a finalized report is sent to a patient, every attempt is recorded here with the number it went to and who sent it."
            />
          }
        />
      </Card>

      <Pagination
        page={shares.page}
        count={shares.count}
        limit={shares.limit}
        onPageChange={shares.setPage}
      />
    </Page>
  );
}
